import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme as useMuiTheme,
    alpha,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TaxableTransaction } from '../../tax-reports/types';
import FilledTableSortLabel from '../../common/FilledTableSortLabel';
import StyledPagination from '../../common/StyledPagination';
import { TransactionIssueType } from '../../../services/transaction.types';
import PulsingIcon from '../../common/PulsingIcon';
import { PendingOutlined } from '@mui/icons-material';
import { useFormatter } from '../../../context/FormatterContext';

interface IssueTransaction extends TaxableTransaction {
    issueType?: 'missingAssetValue' | 'costBasis';
    taxYear?: number;
}

type Order = 'asc' | 'desc';

interface HeadCell {
    id: keyof IssueTransaction | 'type' | 'impact' | 'actions';
    label: string;
    numeric: boolean;
    sortable: boolean;
}

interface IssuesTableProps {
    issues: IssueTransaction[];
    userCurrency: string;
    onIgnore: (transactionId: string | number, issueType: string, currency: string, taxYear?: number) => Promise<void>;
    isIgnored: (transactionId: string | number, taxYear?: number) => boolean;
    order: Order;
    orderBy: string;
    page: number;
    rowsPerPage: number;
    onOrderChange: (order: Order) => void;
    onOrderByChange: (orderBy: string) => void;
    onPageChange: (page: number) => void;
    onRowsPerPageChange: (rowsPerPage: number) => void;
    total: number;
    processingSavedTransactions?: Set<string | number>;
    isRecalculating?: boolean;
    isIgnoreLoading?: Set<string | number>;
    isLoading?: boolean;
}

/**
 * Extracts the real transaction ID from the issue data
 * For missing price issues, the issue ID is the transaction ID
 * For cost basis issues, the real transaction ID is found in pool.trades[0].sale_id
 */
const getRealTransactionId = (issueTransaction: IssueTransaction): number => {
    // For missing price issues, the issue ID is the transaction ID
    if (issueTransaction.issueType === 'missingAssetValue') {
        console.log(`[IssuesTable] Using issue ID ${issueTransaction.id} as transaction ID for missing price issue`);
        return Number(issueTransaction.id);
    }
    
    // For cost basis issues, try to get the real transaction ID from pool.trades[0].sale_id
    const pool = (issueTransaction as any).pool;
    const trades = pool?.trades;
    
    if (trades && Array.isArray(trades) && trades.length > 0) {
        const saleId = trades[0].sale_id;
        if (saleId && saleId > 0) {
            console.log(`[IssuesTable] Found real transaction ID ${saleId} for cost basis issue ${issueTransaction.id}`);
            return saleId;
        }
    }
    
    // Fallback for cost basis issues where we can't find the sale_id
    console.warn(`[IssuesTable] Could not find real transaction ID for cost basis issue ${issueTransaction.id}, using issue ID as fallback`);
    return Number(issueTransaction.id);
};

/**
 * Gets transaction analysis data including real transaction ID and context
 */
const getTransactionAnalysisData = (issueTransaction: IssueTransaction) => {
    const realTransactionId = getRealTransactionId(issueTransaction);
    const pool = (issueTransaction as any).pool;
    const transactionType = pool?.transaction_type;
    const missingAmount = issueTransaction.amount || 0;
    const asset = issueTransaction.currency;
    
    // Determine if this is a fee-related cost basis issue
    const isFeeCostBasisIssue = transactionType === 1000 || // OutVirtualWithdrawalFee
                               transactionType === 1004 || // OutVirtualTradeFee
                               transactionType === 12;     // OutMarginFee
    
    console.log(`[IssuesTable] Transaction analysis data for issue ${issueTransaction.id}:`, {
        realTransactionId,
        transactionType,
        missingAmount,
        asset,
        isFeeCostBasisIssue
    });
    
    return {
        realTransactionId,
        transactionType,
        missingAmount,
        asset,
        isFeeCostBasisIssue
    };
};

/**
 * Determines the correct asset symbol to analyze based on transaction type
 * For cost basis issues, we always use the currency field from the issue data
 */
const getAssetSymbolForAnalysis = (transaction: IssueTransaction): string => {
    // For cost basis issues, always use the currency from the issue data
    // This represents the asset that has the missing cost basis problem
    const assetSymbol = (transaction as any).currencySymbol || transaction.currency;
    
    const analysisData = getTransactionAnalysisData(transaction);
    console.log(`[IssuesTable] Using asset symbol '${assetSymbol}' for analysis of ${analysisData.isFeeCostBasisIssue ? 'fee' : 'sale'} transaction`);
    
    return assetSymbol;
};

const IssuesTable: React.FC<IssuesTableProps> = ({
    issues,
    userCurrency,
    onIgnore,
    isIgnored,
    order,
    orderBy,
    page,
    rowsPerPage,
    onOrderChange,
    onOrderByChange,
    onPageChange,
    onRowsPerPageChange,
    total,
    processingSavedTransactions = new Set(),
    isRecalculating = false,
    isIgnoreLoading = new Set(),
    isLoading = false,
}) => {
    const { t } = useTranslation();
    const theme = useMuiTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { formatTransactionDate, formatCrypto, formatCurrency, formatSmartQuantity} = useFormatter();

    const headCells: HeadCell[] = [
        { id: 'transaction', label: t('smartCheck.table.transaction'), numeric: false, sortable: true },
        { id: 'type', label: t('smartCheck.table.type'), numeric: false, sortable: true },
        { id: 'impact', label: t('smartCheck.table.impact'), numeric: true, sortable: true },
        { id: 'taxYear', label: t('smartCheck.table.taxYear'), numeric: true, sortable: true },
        { id: 'actions', label: t('smartCheck.table.actions'), numeric: false, sortable: false },
    ];

    const formatDate = (dateValue: string | string[] | null | undefined): string => {
        if (!dateValue) return 'N/A';
        
        try {
            const dateStr = Array.isArray(dateValue) ? dateValue[0] : dateValue;
            if (!dateStr) return 'N/A';
            
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return 'N/A';
            
            // Convert to Unix timestamp (seconds) and use formatTransactionDate
            const timestamp = Math.floor(date.getTime() / 1000);
            const formatted = formatTransactionDate(timestamp);
            return formatted.date; // Returns format like "19 Dec 2023"
        } catch (error) {
            return 'N/A';
        }
    };

    const getIssueTypeDisplay = (transaction: IssueTransaction) => {
        if (transaction.issueType === 'costBasis') {
            const impact = Math.abs(transaction.gain || 0);
            if (impact > 10) {
                return {
                    label: t('smartCheck.table.missingCostBasis'),
                    color: theme.palette.error.main, // Red for high impact
                };
            } else {
                return {
                    label: t('smartCheck.table.missingCostBasis'),
                    color: theme.palette.warning.main, // Yellow for low impact
                };
            }
        } else {
            return {
                label: t('smartCheck.table.missingPrice'),
                color: theme.palette.info.main, // Blue for pricing issues
            };
        }
    };

    const getTransactionDescription = (transaction: IssueTransaction): string => {
        const formattedAmount = formatSmartQuantity(transaction.amount);
        const assetSymbol = (transaction as any).currencySymbol || transaction.currency;
        const amount = `${formattedAmount} ${assetSymbol}`;
        const date = transaction.issueType === 'costBasis' 
            ? formatDate(transaction.sold_at)
            : formatDate(transaction.acquired_at);
        const exchange = transaction.issueType === 'costBasis' 
            ? (transaction.sale_exchange || 'N/A')
            : ((transaction as any).origin || 'N/A');
        
        // Create natural language description
        if (transaction.issueType === 'costBasis') {
            return `${amount} verkauft am ${date} via ${exchange}`;
        } else {
            return `${amount} erhalten am ${date} via ${exchange}`;
        }
    };

    // Custom formatter for Smart Check impact display
    const formatSmartCheckImpact = (value: number): string => {
        const formatted = formatCurrency(value);
        // If the formatted result shows 0,00 or 0.00, replace with < 0,01 or < 0.01
        if (formatted.includes('0,00') || formatted.includes('0.00')) {
            return formatted.replace(/0[,.]00/, '< 0,01');
        }
        return formatted;
    };

    const getImpactDisplay = (transaction: IssueTransaction) => {
        if (transaction.issueType === 'costBasis') {
            const impact = Math.abs(transaction.gain || 0);
            const formattedAmount = formatSmartCheckImpact(impact);
            return formattedAmount;
        } else {
            return t('smartCheck.table.unknown');
        }
    };

    const getSortValue = (transaction: IssueTransaction, property: string) => {
        switch (property) {
            case 'transaction':
                // Sort by asset symbol for the combined transaction column
                return (transaction as any).currencySymbol || transaction.currency || '';
            case 'currency':
                return (transaction as any).currencySymbol || transaction.currency || '';
            case 'sold_at':
            case 'date':
                const dateValue = transaction.issueType === 'costBasis' 
                    ? transaction.sold_at 
                    : transaction.acquired_at;
                return dateValue ? new Date(Array.isArray(dateValue) ? dateValue[0] : dateValue).getTime() : 0;
            case 'type':
                return transaction.issueType || '';
            case 'impact':
                return transaction.issueType === 'costBasis' ? Math.abs(transaction.gain || 0) : 0;
            case 'amount':
                return transaction.amount || 0;
            case 'sale_exchange':
                return transaction.issueType === 'costBasis' 
                    ? (transaction.sale_exchange || '')
                    : ((transaction as any).origin || '');
            case 'taxYear':
                return transaction.taxYear || 0;
            default:
                return '';
        }
    };

    const sortedIssues = useMemo(() => {
        const sorted = [...issues].sort((a, b) => {
            const aValue = getSortValue(a, orderBy);
            const bValue = getSortValue(b, orderBy);
            
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return order === 'asc' 
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }
            
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return order === 'asc' ? aValue - bValue : bValue - aValue;
            }
            
            return 0;
        });
        
        return sorted;
    }, [issues, order, orderBy]);

    const paginatedIssues = useMemo(() => {
        const startIndex = page * rowsPerPage;
        return sortedIssues.slice(startIndex, startIndex + rowsPerPage);
    }, [sortedIssues, page, rowsPerPage]);

    const handleRequestSort = (property: string) => {
        const isAsc = orderBy === property && order === 'asc';
        onOrderChange(isAsc ? 'desc' : 'asc');
        onOrderByChange(property);
    };

    const handleChangePage = (_: unknown, newPage: number) => {
        onPageChange(newPage);
    };

    const handleChangeRowsPerPage = (event: any) => {
        onRowsPerPageChange(parseInt(event.target.value, 10));
        onPageChange(0);
    };

    const handleAIReview = (transaction: IssueTransaction) => {
        console.log('[IssuesTable] Navigating to AI Check page for transaction', transaction.id);
        const currentFilter = searchParams.get('filter') || 'all';
        navigate(`/smart-check/issues/ai-check/${transaction.id}?filter=${currentFilter}`);
    };

    const handleViewTransaction = (transaction: IssueTransaction) => {
        console.log(`[IssuesTable] Preparing navigation for transaction:`, transaction);
        
        // Extract filter data from the Smart Check issue
        const asset = transaction.currency; // Mapped from buy_coin
        const transactionDate = transaction.acquired_at; // Mapped from time
        const account = transaction.sale_exchange; // Mapped from origin
        
        if (!asset || !transactionDate) {
            console.error('Missing required filter data:', { asset, transactionDate });
            return;
        }
        
        // Convert date to YYYY-MM-DD format
        const date = new Date(transactionDate);
        const dateStr = date.toISOString().split('T')[0]; // Gets YYYY-MM-DD
        
        console.log(`[IssuesTable] Navigating with filters:`, {
            asset,
            dateStr,
            account,
            issueType: transaction.issueType
        });
        
        // Use precise filtering: asset + date + missing price status
        const navigationOptions = {
            assets: [asset],
            dates: {
                start: dateStr,
                end: dateStr
            }
        };
        
        
        // Optionally add account filter if we have reliable account data
        if (account && account !== 'N/A') {
            navigationOptions.accounts = [account];
        }
        
        // Build URL parameters
        const params = new URLSearchParams();
        
        // Add Smart Check context for navigation highlighting and filtering
        params.set('smartCheckContext', 'true');
        
        // Add filtering for missing price transactions
        if (transaction.issueType === 'missingAssetValue') {
            params.set('missingPriceOnly', 'true'); // Filter to show only missing price transactions
        }
        
        if (navigationOptions.assets?.length) {
            params.set('assets', navigationOptions.assets.join(','));
        }
        if (navigationOptions.dates?.start) {
            params.set('startDate', navigationOptions.dates.start);
        }
        if (navigationOptions.dates?.end) {
            params.set('endDate', navigationOptions.dates.end);
        }
        if (navigationOptions.accounts?.length) {
            params.set('accounts', navigationOptions.accounts.join(','));
        }
        
        const url = `/transactions?${params.toString()}`;
        console.log(`[IssuesTable] Navigating to transactions with Smart Check context:`, url);
        
        // Navigate in same tab
        navigate(url);
    };


    // Don't show stale data while loading fresh issues after recalc
    if (isLoading || issues.length === 0) {
        return null;
    }

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: 'background.paper',
            }}
        >
            <TableContainer sx={{ overflow: 'auto' }}>
                <Table 
                    stickyHeader 
                    size="medium" 
                    sx={{
                        minWidth: '100%',
                        tableLayout: 'auto',
                        width: 'max-content',
                        '& .MuiTableCell-root': {
                            py: 2.5,
                            px: 1.5,
                            borderBottom: '2px solid',
                            borderBottomColor: `${theme.palette.divider}`,
                            borderBottomOpacity: 0.6,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        },
                        '& .MuiTypography-root': {
                            fontSize: { xs: '0.85rem', sm: '0.95rem' },
                        },
                        '& .MuiTableHead-root .MuiTableCell-root': {
                            bgcolor: 'background.paper',
                            fontWeight: 600,
                            borderBottom: '2px solid',
                            borderBottomColor: `${theme.palette.divider}`,
                        },
                        '& .MuiTableBody-root tr:last-child td': {
                            borderBottom: '0 !important',
                        },
                        '& .MuiTableBody-root tr:last-child th': {
                            borderBottom: '0 !important',
                        },
                    }}
                >
                    <colgroup>
                        <col style={{ width: 'auto', minWidth: '250px' }} />
                        <col style={{ width: 'auto', minWidth: '120px' }} />
                        <col style={{ width: 'auto', minWidth: '100px' }} />
                        <col style={{ width: 'auto', minWidth: '80px' }} />
                        <col style={{ width: 'auto', minWidth: '200px' }} />
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            {headCells.map((headCell) => (
                                <TableCell
                                    key={headCell.id}
                                    align={headCell.id === 'actions' ? 'right' : headCell.numeric ? 'right' : 'left'}
                                    sortDirection={orderBy === headCell.id ? order : false}
                                >
                                    {headCell.sortable ? (
                                        <FilledTableSortLabel
                                            active={orderBy === headCell.id}
                                            direction={orderBy === headCell.id ? order : 'asc'}
                                            onClick={() => handleRequestSort(headCell.id as string)}
                                        >
                                            {headCell.label}
                                            {orderBy === headCell.id ? (
                                                <Box component="span" sx={visuallyHidden}>
                                                    {order === 'desc' ? t('common.table.sort.descending') : t('common.table.sort.ascending')}
                                                </Box>
                                            ) : null}
                                        </FilledTableSortLabel>
                                    ) : (
                                        headCell.label
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedIssues.map((transaction) => {
                            const issueType = getIssueTypeDisplay(transaction);
                            const ignored = isIgnored(transaction.id, transaction.taxYear);
                            const isProcessing = processingSavedTransactions.has(transaction.id);
                            const isIgnoreProcessing = isIgnoreLoading.has(transaction.id);
                            
                            // Debug logging
                            if (isProcessing) {
                                console.log('[IssuesTable] Transaction is processing:', transaction.id, 'Processing set:', Array.from(processingSavedTransactions));
                            }
                            
                            return (
                                <TableRow 
                                    key={`${transaction.id}-${transaction.taxYear}`}
                                    sx={{
                                        opacity: ignored ? 0.6 : 1,
                                        backgroundColor: ignored ? alpha(theme.palette.grey[500], 0.05) : 'transparent',
                                    }}
                                >
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {getTransactionDescription(transaction)}
                                        </Typography>
                                    </TableCell>
                                    
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Chip
                                                label={isProcessing ? t('sidebar.status.processing') : issueType.label}
                                                size="small"
                                                sx={{
                                                    backgroundColor: alpha(isProcessing ? theme.palette.info.main : issueType.color, 0.1),
                                                    color: isProcessing ? theme.palette.info.main : issueType.color,
                                                    fontWeight: 500,
                                                    fontSize: '0.75rem',
                                                }}
                                            />
                                            {isProcessing && (
                                                <PulsingIcon 
                                                    IconComponent={PendingOutlined} 
                                                    size={16} 
                                                    color={theme.palette.info.main} 
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                    
                                    <TableCell align="right">
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                fontWeight: transaction.issueType === 'costBasis' ? 600 : 400,
                                                color: transaction.issueType === 'costBasis' && Math.abs(transaction.gain || 0) > 10
                                                    ? theme.palette.error.main 
                                                    : 'text.primary'
                                            }}
                                        >
                                            {getImpactDisplay(transaction)}
                                        </Typography>
                                    </TableCell>
                                    
                                    <TableCell align="right">
                                        <Typography variant="body2">
                                            {transaction.taxYear || 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                                            {isProcessing ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <PulsingIcon 
                                                        IconComponent={PendingOutlined} 
                                                        size={20} 
                                                        color={theme.palette.info.main} 
                                                    />
                                                    <Typography variant="body2" color="info.main">
                                                        {t('sidebar.status.processing')}
                                                    </Typography>
                                                </Box>
                                            ) : !ignored ? (
                                                <>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={() => transaction.issueType === 'costBasis' ? handleAIReview(transaction) : handleViewTransaction(transaction)}
                                                        sx={{ minWidth: 80 }}
                                                    >
                                                        {transaction.issueType === 'costBasis'
                                                            ? t('smartCheck.table.aiReview')
                                                            : t('smartCheck.table.viewTransaction')
                                                        }
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        color="secondary"
                                                        onClick={() => onIgnore(
                                                            transaction.id, 
                                                            transaction.issueType || 'costBasis',
                                                            transaction.currency,
                                                            transaction.taxYear
                                                        )}
                                                        disabled={isIgnoreProcessing}
                                                        sx={{ minWidth: 60 }}
                                                    >
                                                        {isIgnoreProcessing ? t('common.loading') : t('smartCheck.table.ignore')}
                                                    </Button>
                                                </>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => onIgnore(
                                                        transaction.id, 
                                                        transaction.issueType || 'costBasis',
                                                        transaction.currency,
                                                        transaction.taxYear
                                                    )}
                                                    disabled={isIgnoreProcessing}
                                                    sx={{ minWidth: 60 }}
                                                >
                                                    {isIgnoreProcessing ? t('common.loading') : t('smartCheck.table.unignore')}
                                                </Button>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <StyledPagination
                rowsPerPageOptions={[10, 25, 50]}
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t('common.table.pagination.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t('common.table.pagination.of')} ${count}`}
            />
        </Box>
    );
};

export default IssuesTable;